import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { ApiError, ApiService, AutoUpdatedFirmReportZipModel } from 'api';
import { MeasurementDate } from 'common/types/measurementDate';
import { NOT_FOUND_STATUS_CODE } from 'layouts/Main/components/ExcelExportDialogV2/constants';
import { usePollingByInterval } from 'services/hooks';
import { Logger } from 'utilities';

const STATUS_POLLING_INTERVAL_MS = 5000;

const useAutoUpdatedFirmZip = ({
  selectedReportDate,
  isVirtualRecentDateSelectedForReport,
  firmId,
  componentIsVisible,
}: {
  selectedReportDate: MeasurementDate | null;
  isVirtualRecentDateSelectedForReport: boolean;
  firmId: number | null;
  componentIsVisible: boolean;
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [statusPollingInterval, setStatusPollingInterval] = useState<number | null>(null);
  const [autoUpdatedZipStatus, setAutoUpdatedZipStatus] = useState<AutoUpdatedFirmReportZipModel | null>(null);
  const isButtonDisabled = useMemo(
    () => selectedReportDate === null || isVirtualRecentDateSelectedForReport,
    [selectedReportDate, isVirtualRecentDateSelectedForReport]
  );
  const isConcreteDateSelectedForReport = useMemo(
    () => selectedReportDate !== null && !isVirtualRecentDateSelectedForReport,
    [selectedReportDate, isVirtualRecentDateSelectedForReport]
  );

  const retrieveAutoUpdatedFirmZipStatus = useCallback(async () => {
    try {
      return await ApiService.apiExcelExportsFullCompanyReportsAllReportsRead(
        firmId!.toString(),
        selectedReportDate!.date
      );
    } catch (error) {
      if (error instanceof ApiError && error.status === NOT_FOUND_STATUS_CODE) {
        return null;
      }
      Logger.error('Error in retrieveFullCompanyReportsStatuses API call', error);
      enqueueSnackbar('Error while retrieving the status of the auto updated zip firm', { variant: 'error' });
      return null;
    }
  }, [firmId, selectedReportDate, enqueueSnackbar]);

  const refreshAutoUpdatedZipStatus = useCallback(async () => {
    const autoUpdatedZipStatus = await retrieveAutoUpdatedFirmZipStatus();
    setAutoUpdatedZipStatus(autoUpdatedZipStatus);
  }, [retrieveAutoUpdatedFirmZipStatus]);

  useEffect(() => {
    if (firmId && isConcreteDateSelectedForReport && componentIsVisible) {
      refreshAutoUpdatedZipStatus();
    }
  }, [firmId, refreshAutoUpdatedZipStatus, isConcreteDateSelectedForReport, componentIsVisible]);

  useEffect(() => {
    if (componentIsVisible && autoUpdatedZipStatus) {
      setStatusPollingInterval(STATUS_POLLING_INTERVAL_MS);
    } else {
      setStatusPollingInterval(null);
    }
  }, [setStatusPollingInterval, componentIsVisible, autoUpdatedZipStatus]);

  usePollingByInterval(refreshAutoUpdatedZipStatus, statusPollingInterval);

  return {
    isButtonDisabled,
    autoUpdatedZipStatus,
    retrieveAutoUpdatedFirmZipStatus,
  };
};

export default useAutoUpdatedFirmZip;
